import { createSelector, createSlice } from "@reduxjs/toolkit";
import { useMemo } from "react";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, DefaultFailureAction, DefaultSuccessAction } from "../common/reducers/utilities";
import { ensureArray, groupBy } from "../common/utils/arrayUtilities";
import { markFetched } from "../common/hooks/initHook";
import { clientConfig } from './clientConfigSlice';

const positionSlice = createSlice({
    name: 'positions',
    initialState: {
        defaultPositions: [],
        clientPositions: {},
        locationPositions: {}
    },
    reducers: {
        setDefaultPositions: (state, action) => {
            state.defaultPositions = action.payload;
        },
        addOrUpdateDefaultPosition: (state, action) => {
            const toUpdate = ensureArray(action.payload);
            const ids = new Set(toUpdate.map(p => p.id));
            state.defaultPositions = state.defaultPositions.filter(p => !ids.has(p.id)).concat(toUpdate);
        },
        removeDefaultPosition: (state, action) => {
            const ids = new Set(ensureArray(action.payload).map(p => p.id));
            if (state.defaultPositions.some(p => ids.has(p.id))) {
                state.defaultPositions = state.defaultPositions.filter(p => !ids.has(p.id));
            }
        },
        setClientPositions: (state, action) => {
            const toSet = ensureArray(action.payload);
            const byClient = groupBy(toSet, 'clientid');
            Object.entries(byClient).forEach(([ clientid, positions ]) => {
                state.clientPositions[clientid] = positions;
            });
        },
        addOrUpdateClientPosition: (state, action) => {
            const toUpdate = ensureArray(action.payload);
            const byClient = groupBy(toUpdate, 'clientid');
            Object.entries(byClient).forEach(([ clientid, positions ]) => {
                const ids = new Set(positions.map(p => p.id));
                state.clientPositions[clientid] = (state.clientPositions[clientid]?.filter(p => !ids.has(p.id)) ?? []).concat(positions);
            });
        },
        removeClientPosition: (state, action) => {
            const toRemove = ensureArray(action.payload);
            const byClient = groupBy(toRemove, 'clientid');
            Object.entries(byClient).forEach(([ clientid, positions ]) => {
                const ids = new Set(positions.map(p => p.id));
                if (state.clientPositions[clientid]) {
                    state.clientPositions[clientid] = state.clientPositions[clientid].filter(p => !ids.has(p.id));
                }
                if (state.clientPositions[clientid]?.length === 0) {
                    delete state.clientPositions[clientid];
                }
            });
        },
        setLocationPositions: (state, action) => {
            const toSet = ensureArray(action.payload);
            const byLocation = groupBy(toSet, 'locationid');
            Object.entries(byLocation).forEach(([ locationid, positions ]) => {
                state.locationPositions[locationid] = positions;
            });
        },
        addOrUpdateLocationPosition: (state, action) => {
            const toUpdate = ensureArray(action.payload);
            const byLocation = groupBy(toUpdate, 'locationid');
            Object.entries(byLocation).forEach(([ locationid, positions ]) => {
                const ids = new Set(positions.map(p => p.id));
                state.locationPositions[locationid] = (state.locationPositions[locationid]?.filter(p => !ids.has(p.id)) ?? []).concat(positions);
            });
        },
        removeLocationPosition: (state, action) => {
            const toRemove = ensureArray(action.payload);
            const byLocation = groupBy(toRemove, 'locationid');
            Object.entries(byLocation).forEach(([ locationid, positions ]) => {
                const ids = new Set(positions.map(p => p.id));
                if (state.locationPositions[locationid]) {
                    state.locationPositions[locationid] = state.locationPositions[locationid].filter(p => !ids.has(p.id));
                }
                if (state.locationPositions[locationid]?.length === 0) {
                    delete state.locationPositions[locationid];
                }
            });
        },
    },
    extraReducers: builder => {
        builder.addCase(logout, () => positionSlice.getInitialState())
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'position_other', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'position_other');

const fetchDefaultPositions = network => network.get('/api/v1/defaultPositions', positionSlice.actions.setDefaultPositions, visualizeFailure('get'));
const fetchAllLocationPositions = network => network.get('/api/v1/locationPositions/', positionSlice.actions.setLocationPositions, visualizeFailure('get'));
const fetchAllClientPositions = network => network.get('/api/v1/clientPositions/', positionSlice.actions.setClientPositions, visualizeFailure('get'));

export const createDefaultPosition = (network, data) => network.post('/api/v1/defaultPosition', data, positionSlice.actions.addOrUpdateDefaultPosition, visualizeFailure('create'));
export const updateDefaultPosition = (network, data) => network.put('/api/v1/defaultPosition', data, positionSlice.actions.addOrUpdateDefaultPosition, visualizeFailure('update'));
export const removeDefaultPosition = (network, id) => network.delete('/api/v1/defaultPosition/' + encodeURIComponent(id), positionSlice.actions.removeDefaultPosition, visualizeFailure('remove'));

export const createClientPosition = (network, data) => network.post('/api/v1/clientPosition', data, positionSlice.actions.addOrUpdateClientPosition, visualizeFailure('create'));
export const updateClientPosition = (network, data) => network.put('/api/v1/clientPosition', data, positionSlice.actions.addOrUpdateClientPosition, visualizeFailure('update'));
export const removeClientPosition = (network, id) => network.delete('/api/v1/clientPosition/' + encodeURIComponent(id), positionSlice.actions.removeClientPosition, visualizeFailure('remove'));

export const createLocationPosition = (network, data) => network.post('/api/v1/locationPosition', data, positionSlice.actions.addOrUpdateLocationPosition, visualizeFailure('create'));
export const updateLocationPosition = (network, data) => network.put('/api/v1/locationPosition', data, positionSlice.actions.addOrUpdateLocationPosition, visualizeFailure('update'));
export const removeLocationPosition = (network, id) => network.delete('/api/v1/locationPosition/' + encodeURIComponent(id), positionSlice.actions.removeLocationPosition, visualizeFailure('remove'));
// #endregion

// #region selectors
const selectDefaultPositions = state => state.positions.defaultPositions;
const selectLocationPositionsData = state => state.positions.locationPositions;
const selectClientPositionsData = state => state.positions.clientPositions;
const selectAllLocationPositionsFlattened = createSelector([selectLocationPositionsData], positions => Object.values(positions).flat());
const selectAllClientPositionsFlattened = createSelector([selectClientPositionsData], positions => Object.values(positions).flat());
const selectLocationPositions = locationid => createSelector([selectLocationPositionsData], positions => positions[locationid] ?? []);
const selectClientPositions = clientid => createSelector([selectClientPositionsData], positions => positions[clientid] ?? []);

// #endregion

// #region listeners
const positionListeners = {
    positionCreated: (position, { dispatch, websocket }) => {
        markFetched(websocket, 'position', position.id)
        dispatch(positionSlice.actions.addOrUpdateDefaultPosition(position))
    },
    positionUpdated: (position, { dispatch }) => dispatch(positionSlice.actions.addOrUpdateDefaultPosition(position)),
    positionRemoved: (position, { dispatch }) => dispatch(positionSlice.actions.removeDefaultPosition(position)),
}
const locationListeners = {
    locationPositionCreated: (position, { dispatch, websocket }) => {
        markFetched(websocket, 'position', position.id)
        dispatch(positionSlice.actions.addOrUpdateLocationPosition(position))
    },
    locationPositionUpdated: (position, { dispatch }) => dispatch(positionSlice.actions.addOrUpdateLocationPosition(position)),
    locationPositionRemoved: (position, { dispatch }) => dispatch(positionSlice.actions.removeLocationPosition(position))
}
const clientListeners = {
    clientPositionCreated: (position, { dispatch, websocket }) => {
        markFetched(websocket, 'position', position.id)
        dispatch(positionSlice.actions.addOrUpdateClientPosition(position))
    },
    clientPositionUpdated: (position, { dispatch }) => dispatch(positionSlice.actions.addOrUpdateClientPosition(position)),
    clientPositionRemoved: (position, { dispatch }) => dispatch(positionSlice.actions.removeClientPosition(position))
}
// #endregion

const initDefaultPositions = { run: fetchDefaultPositions, listen: positionListeners, immortal: true };
const initLocationPositions = { run: fetchAllLocationPositions, listen: locationListeners, immortal: true };
const initClientPositions = { run: fetchAllClientPositions, listen: clientListeners, immortal: true }

export const canRoutePositions = ({credentials, clientConfig}) => credentials.hasRole(['administrator', 'auditor','rostering manager', 'recurring rostering manager','rostering auditor', 'recurring rostering auditor','event manager','event auditor']) && clientConfig.rosterEnablePositions;

export const [useDefaultPositions, realizeDefaultPositions] = dataHookFactory(selectDefaultPositions, initDefaultPositions);
export const [useAllLocationPositions, realizeAllLocationPositions] = dataHookFactory(selectAllLocationPositionsFlattened, initClientPositions);
export const [useLocationPositions] = dataHookFactory(selectLocationPositions, ids => initLocationPositions);
export const [useAllClientPositions, realizeAllClientPositions] = dataHookFactory(selectAllClientPositionsFlattened, initClientPositions);
export const [useClientPositions] = dataHookFactory(selectClientPositions, ids => initClientPositions);

export const usePositions = (clientid, locationid) => {
    const defaultPositions = useDefaultPositions();
    const clientPositions = useClientPositions(clientid);
    const locationPositions = useLocationPositions(locationid);
    
    if (locationPositions?.length > 0) {
        return locationPositions;
    }
    if (clientPositions?.length > 0) {
        return clientPositions;
    }
    return defaultPositions;
}

export const useAllPositions = () => {
    const allLocationPositions = useAllLocationPositions();
    const allClientPositions = useAllClientPositions()
    const defaultPositions = useDefaultPositions();

    return useMemo(()=> [...allLocationPositions, ...allClientPositions, ...defaultPositions],[allClientPositions, allLocationPositions, defaultPositions]);
}

export const realizeAllPositions = async (realizationContext) => {
    const allLocationPositions = await realizeAllLocationPositions(realizationContext);
    const allClientPositions = await realizeAllClientPositions(realizationContext);
    const defaultPositions = await realizeDefaultPositions(realizationContext);

    return [...allLocationPositions, ...allClientPositions, ...defaultPositions]
}

export const realizePosition = async (realizationContext, id) => {
    let result = (await realizeAllLocationPositions(realizationContext))?.find(p => p.id == id);
    if (!result) {
        result = (await realizeAllClientPositions(realizationContext))?.find(p => p.id == id);
    }
    if (!result) {
        result = (await realizeDefaultPositions(realizationContext))?.find(p => p.id == id);
    }
    return result;
}

export const usePosition = (id) => {
    const allLocationPositions = useAllLocationPositions();
    const allClientPositions = useAllClientPositions()
    const defaultPositions = useDefaultPositions();

    return useMemo(() => {
        return allLocationPositions?.find(p => p.id == id) ?? allClientPositions?.find(p => p.id == id) ?? defaultPositions?.find(p => p.id == id)
    }, [allLocationPositions, allClientPositions, defaultPositions, id])
}

const reducer = { name: positionSlice.name, reducer: positionSlice.reducer }
export default reducer;